import { darken } from "polished";
import { forwardRef, useEffect, useState } from "react";
import styled from "styled-components";
import { faCircle } from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";

import { scrollUp } from "common/utils";

import { RightPanel } from "common/components/core/rightPanel";
import { Tooltip } from "common/components/tooltip";

export const NotificationPopup = forwardRef(({
    forceOpen,
    icon,
    children,
    title,
    hasIndicator,
    iconStyle,
    onVisibilityChange,
    onOpen
}, ref) => {
    const [expanded, setExpanded] = useState(forceOpen);
    const [closeOnClick, setCloseOnClick] = useState(false);
    
    useEffect(() => {
        onVisibilityChange(expanded);
        if (expanded) {
            onOpen();
        }
    }, [expanded, onOpen, onVisibilityChange]);
    
    useEffect(() => {
        if (forceOpen) {
            scrollUp();
            setExpanded(true);
        }
    }, [forceOpen]);
    
    const withSetExpanded = (newValue) => {
        // Wait 1s before allowing to click outside to close the panel
        setTimeout(() => setCloseOnClick(newValue), 1000);
        setExpanded(newValue);
    };
    
    return (
        <TopbarIcon onClick={() => withSetExpanded(true)}>
            <Tooltip title={title} position="left">
                <Icon data-indicator={hasIndicator} $indicator={hasIndicator} $iconStyle={iconStyle}>
                    <FontAwesomeIcon icon={icon}/>
                    {hasIndicator && <IndicatorIcon />}
                </Icon>
            </Tooltip>
            <RightPanelNotification
                ref={ref}
                open={expanded}
                onClose={() => withSetExpanded(false)}
                closeOnDocumentClick={closeOnClick}
                closeOnEscape
                repositionOnResize
            >
                {children}
            </RightPanelNotification>
        </TopbarIcon>
    );
});

export const TopbarIcon = styled.div`
    width: 44px;
    height: 40px;
    font-size: ${(props) => props.theme.headerFontSize};
    border-radius: 20px;
    margin: 0 2px;

    &:hover {
        background: ${(props) => props.theme.colors["canvas-mid"]};
    }
`;

const RightPanelNotification = styled(RightPanel)`
    &-content {
        margin: auto;
        padding: 0;
        width: 400px;
        z-index: 1999;

        .action-bar {
            height: 40px;
            padding: 10px 15px;
            display: flex;
            justify-content: space-between;

            .badge {
                cursor: pointer;
                background: ${(props) => props.theme.colors["blue-wcag-327"]};
            }

            .mark-read {
                display: flex;
                cursor: pointer;

                span {
                    padding: 2px 5px;
                    font-size: ${(props) => props.theme.fontSizeMedium};
                }

                div {
                    font-size: ${(props) => props.theme.fontSizeLarge};
                }
            }
        }
    }
`;

export const Icon = styled.div`
    position: relative;
    width: 44px;

    &:hover {
        cursor: pointer;
        color: ${(props) => props.theme.colors["black-wcag-000"]};
    }

    ${({ $indicator, theme }) => $indicator && `
        color: ${theme.colors["pazien-black"]};
    `}

    ${({ $indicator, theme }) => !$indicator && `
        color: ${theme.colors["gray-new"]};
    `}

    ${({ $iconStyle, theme }) => $iconStyle === "reports" && `
        padding: 6px 0 5px 10px;
        font-size: 18px;
        
        ${IndicatorIcon} {
            color: ${darken(0.2, theme.colors["purple-mid"])};
            left: 27px;
        }
    `}

    ${({ $iconStyle, theme }) => $iconStyle === "downloads" && `
        padding: 5px 0 5px 12px;
        
        ${IndicatorIcon} {
            color: ${theme.colors["green-leaf"]};
            left: 23px;
        }
    `}

    ${({ $iconStyle, theme }) => $iconStyle === "announcements" && `
        padding: 5px 0 5px 9px;
        
        ${IndicatorIcon} {
            color: ${theme.colors["blue-light"]};
            left: 26px;
        }
    `}

    ${({ $iconStyle, theme }) => $iconStyle === "university" && `
        padding: 5px 0 5px 15px;
        
        ${IndicatorIcon} {
            color: ${theme.colors.jason};
            left: 23px;
        }
    `}
    
    ${({ $iconStyle }) => $iconStyle === "mimic" && `
        padding: 5px 0 5px 13px;
        
        ${IndicatorIcon} {
            display: none;
        }
    `}

    ${({ $iconStyle, theme }) => $iconStyle === "alerts" && `
         padding: 5px 0 5px 13px;
        
        ${IndicatorIcon} {
            color: ${theme.colors["wcag-red"]};
            left: 22px;
        }
    `}
`;

const IndicatorIcon = styled(FontAwesomeIcon).attrs({ icon: faCircle })`
    position: absolute;
    top: 5px;
    font-size: ${({ theme }) => theme.fontSizeSmall};
`;

export const PanelHeader = styled.div`
    display: flex;
    justify-content: space-between;
    width: 100%;
    padding: 15px;
    height: 64px;
    background-color: ${(props) => props.theme.colors["canvas-white"]};
    font-size: ${({ theme }) => theme.headerFontSize};
    color: ${(props) => props.theme.colors["gray-dark"]};
    border-bottom: 1px solid ${(props) => props.theme.colors["gray-soft"]};
`;

export const PanelIcon = styled.div`
    cursor: pointer;
    font-size: ${({ theme }) => theme.headerFontSizeMedium};
`;

export const ManageIcon = styled(PanelIcon)`
    margin-right: 20px;
    padding-top: 3px;
`;

export const Title = styled.div`
    font-size: ${({ theme }) => theme.headerFontSize};
`;

export const HeaderActions = styled.div`
    position: relative;
    top: -5px;
    display: flex;
    color: ${(props) => props.theme.colors["gray-light"]};
`;

export const FullHeightList = styled.div`
    max-height: calc(100vh - 160px);
`;

export const NoDataAvailable = styled.div`
    padding: 60px 20px;
    text-align: center;
    font-size: ${({ theme }) => theme.headerFontSize};
    color: ${(props) => props.theme.colors["gray-mid"]};
`;

export const RowItem = styled.div`
    display: flex;
    justify-content: space-between;
    padding: 10px;
    background: ${(props) => props.theme.colors["canvas-white"]};
    border-bottom: 1px solid ${(props) => props.theme.colors["gray-soft"]};

    .icon {
        width: 36px;
        padding: 8px 10px;
        &.alert {
            font-size: ${({ theme }) => theme.headerFontSize};
            color: ${(props) => props.theme.colors.brick};
        }
        &.download {
            font-size: ${({ theme }) => theme.headerFontSize};
            color: ${(props) => props.theme.colors["blue-light"]};
        }

        &.close {
            font-size: ${({ theme }) => theme.headerFontSize};
            color: ${(props) => props.theme.colors["gray-mid"]};
        }
        
        &:hover {
            cursor: pointer;
        }
    }

    .info {
        width: 300px;
        
        &.snap-link {
            cursor: pointer;
        }

        .title {
            font-size: ${({ theme }) => theme.fontSizeLarge};
            font-family: ${({ theme }) => theme.text.medium};
        }
        .msg {
            font-size: ${({ theme }) => theme.fontSizeLarge};
        }
        .date {
            font-size: ${({ theme }) => theme.fontSizeLarge};
            font-family: ${({ theme }) => theme.text.light};
        }
    }
`;

NotificationPopup.defaultProps = {
    forceOpen: false,
    onOpen: () => {}
};
